// src/context/AuthProvider.js
import React, { useState, useEffect, createContext, useContext } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  browserLocalPersistence,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { colorPalettes } from "../palettes"; // Import the color palettes

// Find the Slate palette
const slatePalette = colorPalettes.basic.palettes.find(p => p.name === 'Slate');

// Create a default palette object with 35% opacity
const defaultPalette = {
  ...slatePalette,
  opacity: 25,
  isReversed: false,
  linkOpacity: 80 // Standard link opacity
};

// Create the auth context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Helper function to validate usernames
const validateUsername = (username) => {
  if (!username || !username.trim()) {
    return { valid: false, message: "Username is required" };
  }

  const usernameRegex = /^[a-zA-Z0-9_]+$/;
  if (!usernameRegex.test(username)) {
    return {
      valid: false,
      message: "Username can only contain letters, numbers, and underscores",
    };
  }

  if (username.length < 3 || username.length > 20) {
    return {
      valid: false,
      message: "Username must be between 3 and 20 characters",
    };
  }

  return { valid: true, message: "" };
};

// Format Firebase error messages for user-friendly display
const formatFirebaseError = (error) => {
  if (!error || !error.code) {
    return "An unexpected error occurred";
  }

  switch (error.code) {
    case "auth/wrong-password":
      return "The password you entered is incorrect";
    case "auth/user-not-found":
      return "No account found with this email";
    case "auth/invalid-email":
      return "Please enter a valid email";
    case "auth/email-already-in-use":
      return "This email is already registered";
    case "auth/weak-password":
      return "Password is too weak";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later";
    case "auth/invalid-credential":
      return "Invalid credentials. Please check your email and password";
    case "auth/popup-blocked":
      return "Popup was blocked. Please allow popups or try on mobile";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Sign-in was cancelled";
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again";
    case "auth/account-exists-with-different-credential":
      return "An account already exists with the same email address but different sign-in credentials. Try signing in using a different method.";
    case "auth/operation-not-allowed":
      return "This operation is not allowed. Please contact support.";
    case "auth/requires-recent-login":
      return "This operation requires a more recent login. Please sign out and sign in again.";
    default:
      return error.message || "Authentication error";
  }
};

// Main AuthProvider component
export const AuthProvider = ({ children }) => {
  // Core authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Authentication flow states
  const [error, setError] = useState("");
  const [verificationPending, setVerificationPending] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);

  const navigate = useNavigate();
  
  // Reset state and errors
  const resetState = () => {
    setError("");
    setVerificationPending(false);
    setVerificationSuccess(false);
    setShowVerificationPrompt(false);

    // Clear localStorage items
    const authItems = [
      "googleSignInPending",
      "isAuthWithGoogle",
      "sessionExpiry",
      "redirectComplete",
      "pendingGoogleSignupUsername",
      "pendingGoogleDisplayUsername"
    ];

    authItems.forEach((item) => localStorage.removeItem(item));
  };

  // Ensure persistence is set on mount
  useEffect(() => {
    const setPersistenceAndListenForAuthChanges = async () => {
      try {
        await auth.setPersistence(browserLocalPersistence);
        console.log("Persistence set to browserLocalPersistence");
      } catch (error) {
        console.error("Error setting persistence:", error);
      }
    };

    setPersistenceAndListenForAuthChanges();
  }, []);

  // Check for Google redirect result on component mount
  useEffect(() => {
    const handleRedirectResult = async () => {
      if (localStorage.getItem("googleSignInPending") === "true") {
        try {
          setLoading(true);
          const result = await getRedirectResult(auth);

          if (result && result.user) {
            // Google sign-in was successful
            console.log("Google redirect sign-in successful");

            // Check if user exists in Firestore
            const userDoc = await getDoc(doc(db, "users", result.user.uid));

            if (userDoc.exists()) {
              // User exists, proceed to dashboard
              navigate("/dashboard");
            } else {
              // New user - needs to create username
              const pendingUsername = localStorage.getItem(
                "pendingGoogleSignupUsername"
              );
              
              const displayUsername = localStorage.getItem(
                "pendingGoogleDisplayUsername"
              ) || pendingUsername;

              if (pendingUsername) {
                // We have a username, create user document
                await setDoc(doc(db, "users", result.user.uid), {
                  username: pendingUsername, // This is already lowercase from signUpWithGoogle
                  displayUsername: displayUsername, // Original case for display
                  email: result.user.email,
                  photoURL: result.user.photoURL || "",
                  createdAt: new Date(),
                  tierType: "free",
                  emailVerified: false,
                  profile: {
                    displayName: "",
                    bio: "",
                    textColor: "#444444",
                    textOpacity: 70,
                    icons: [],
                    links: [],
                    palette: defaultPalette,
                    theme: "classic",
                    banner: null,
                    public: true,
                    ghostMode: {
                      enabled: false,
                      password: "",
                      failedAttempts: {},
                      lastPasswordUpdate: null,
                    },
                  },
                });

                // Create username document with lowercase username
                await setDoc(doc(db, "usernames", pendingUsername), {
                  userId: result.user.uid,
                  createdAt: new Date(),
                });

                navigate("/dashboard");
              } else {
                // No username, sign out and prompt for username
                await signOut(auth);
                setError("Please complete your registration with a username");
                navigate("/");
              }
            }
          } else if (!result && localStorage.getItem("googleSignInPending") === "true") {
            // No result but we were expecting one - likely an error
            setError("Google sign-in was cancelled or failed. Please try again.");
            navigate("/");
          }

          // Clean up localStorage
          localStorage.removeItem("googleSignInPending");
          localStorage.removeItem("pendingGoogleSignupUsername");
          localStorage.removeItem("pendingGoogleDisplayUsername");
        } catch (error) {
          console.error("Error handling redirect result:", error);
          setError(formatFirebaseError(error));
          navigate("/");
        } finally {
          setLoading(false);
        }
      }
    };

    handleRedirectResult();
  }, [navigate]);

  // Enhanced auth state change handler
  useEffect(() => {
    console.log("Setting up auth state listener");

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth state changed:", user ? "User logged in" : "No user");
      setLoading(true);

      if (user) {
        // Force reload the user to get latest verification status
        await user.reload();
        setCurrentUser(user);
        console.log(
          "User ID:",
          user.uid,
          "Email verified:",
          user.emailVerified
        );

        // Check session expiry
        const expiry = localStorage.getItem("sessionExpiry");
        if (expiry && parseInt(expiry) < Date.now()) {
          console.log("Session expired, signing out");
          localStorage.removeItem("sessionExpiry");
          await signOut(auth);
          setCurrentUser(null);
          setUserDetails(null);
          setLoading(false);
          navigate("/");
          return;
        }

        // Check if this is a Google user
        const isGoogleUser =
          user.providerData &&
          user.providerData.some((p) => p.providerId === "google.com");

        if (isGoogleUser) {
          // Google users are pre-verified
          setVerificationPending(false);
        } else if (!user.emailVerified) {
          // Non-Google users need email verification
          console.log("User email not verified");
          setVerificationPending(true);
          setVerificationEmail(user.email);

          // KEEP THE USER SIGNED IN, just set verification pending
        }

        try {
          console.log("Fetching user details from Firestore");
          const userDoc = await getDoc(doc(db, "users", user.uid));

          if (userDoc.exists()) {
            console.log("User details found");
            const userData = userDoc.data();

            setUserDetails(userData);
          } else {
            console.log("No user document exists in Firestore");

            // For Google users, we'll create a user document if they have a username
            if (isGoogleUser) {
              const pendingUsername = localStorage.getItem(
                "pendingGoogleSignupUsername"
              );
              
              const displayUsername = localStorage.getItem(
                "pendingGoogleDisplayUsername"
              ) || pendingUsername;

              if (pendingUsername) {
                // Create user document
                await setDoc(doc(db, "users", user.uid), {
                  username: pendingUsername, // Already lowercase
                  displayUsername: displayUsername, // Original case
                  email: user.email,
                  photoURL: user.photoURL || "",
                  createdAt: new Date(),
                  tierType: "free",
                  emailVerified: false,
                  profile: {
                    displayName: "",
                    bio: "",
                    textColor: "#444444",
                    textOpacity: 70,
                    icons: [],
                    links: [],
                    palette: defaultPalette,
                    theme: "classic",
                    banner: null,
                    public: true,
                    ghostMode: {
                      enabled: false,
                      password: "",
                      failedAttempts: {},
                      lastPasswordUpdate: null,
                    },
                  },
                });

                // Create username document
                await setDoc(doc(db, "usernames", pendingUsername), {
                  userId: user.uid,
                  createdAt: new Date(),
                });

                localStorage.removeItem("pendingGoogleSignupUsername");
                localStorage.removeItem("pendingGoogleDisplayUsername");

                // Get the user document again
                const newUserDoc = await getDoc(doc(db, "users", user.uid));
                if (newUserDoc.exists()) {
                  setUserDetails(newUserDoc.data());
                }
              } else {
                // No username for Google user, sign out
                await signOut(auth);
                setCurrentUser(null);
                setUserDetails(null);
                setError("Account setup incomplete. Please sign up again.");
              }
            }
          }
        } catch (error) {
          console.error("Error getting user details:", error);
        }
      } else {
        console.log("Setting currentUser and userDetails to null");
        setCurrentUser(null);
        setUserDetails(null);
      }

      console.log("Setting loading to false");
      setLoading(false);
    });

    return unsubscribe;
  }, [navigate]);

  /*
   * Email/Password Sign In
   */
  const signIn = async (identifier, password, rememberMe = true) => {
    setLoading(true);
    setError("");
    try {
      console.log("Attempting sign in for:", identifier);

      if (!rememberMe) {
        const expiry = Date.now() + 45 * 60 * 1000; // 45 minutes
        localStorage.setItem("sessionExpiry", expiry.toString());
      } else {
        localStorage.removeItem("sessionExpiry");
      }

      let email = identifier;

      // If identifier doesn't include @, assume it's a username
      if (!identifier.includes("@")) {
        console.log("Identifier is a username, looking up email");
        
        // Convert username to lowercase for lookup
        const lowerUsername = identifier.trim().toLowerCase();

        // Query for the username document using lowercase
        const usernameDocRef = doc(db, "usernames", lowerUsername);
        const usernameDoc = await getDoc(usernameDocRef);

        if (!usernameDoc.exists()) {
          console.log("Username not found:", lowerUsername);
          setError("No account found with this username");
          setLoading(false);
          return;
        }

        const userId = usernameDoc.data().userId;
        console.log("Found userId for username:", userId);

        // Get the user document to find the email
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists() || !userDoc.data().email) {
          console.log("User document or email not found for userId:", userId);
          setError("Account data incomplete. Please contact support.");
          setLoading(false);
          return;
        }

        email = userDoc.data().email;
        console.log("Found email for username:", email);
      }

      // Set auth domain for this session
      if (typeof window !== "undefined" && window.sessionStorage) {
        sessionStorage.setItem(
          "firebase:AuthEventDomain",
          window.location.hostname
        );
      }

      // Sign in with email/password
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Reload the user to get fresh verification status
      await userCredential.user.reload();

      // Check if the user is verified
      if (!userCredential.user.emailVerified) {
        console.log("Email not verified, showing verification notice");
        setVerificationPending(true);
        setVerificationEmail(email);

        // Sign out the user after a short delay
        setTimeout(async () => {
          try {
            await signOut(auth);
            setCurrentUser(null);
          } catch (error) {
            console.error("Error during automatic sign out:", error);
          }
        }, 500);

        navigate("/verification-notice");
        setLoading(false);
        return;
      }

      setVerificationPending(false);
      console.log("Sign in successful, navigating to dashboard");
      navigate("/dashboard");
    } catch (error) {
      console.error("Sign In Error:", error);
      setError(formatFirebaseError(error));
    } finally {
      setLoading(false);
    }
  };

  /*
   * Email/Password Sign Up with delayed logout
   */
  const signUp = async (username, email, password) => {
    setLoading(true);
    setError("");

    try {
      console.log("Starting email/password signup");

      // Store lowercase version of username
      const lowerUsername = username.trim().toLowerCase();
      const originalUsername = username.trim(); // Keep original for display

      // Validate username
      const usernameValidation = validateUsername(username);
      if (!usernameValidation.valid) {
        setError(usernameValidation.message);
        setLoading(false);
        return;
      }

      // Check username availability using lowercase
      console.log("Checking if username is available:", lowerUsername);
      const usernameDoc = await getDoc(doc(db, "usernames", lowerUsername));
      if (usernameDoc.exists()) {
        setError("Username is already taken");
        setLoading(false);
        return;
      }

      // Set auth domain for this session
      if (typeof window !== "undefined" && window.sessionStorage) {
        sessionStorage.setItem(
          "firebase:AuthEventDomain",
          window.location.hostname
        );
      }

      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      console.log(
        "User created successfully in Auth:",
        userCredential.user.uid
      );

      // Create user document in Firestore
      await setDoc(doc(db, "users", userCredential.user.uid), {
        username: lowerUsername,
        displayUsername: originalUsername, // Store original case for display
        email: email,
        photoURL: "",
        createdAt: new Date(),
        tierType: "free",
        emailVerified: false,
        profile: {
          displayName: "",
          bio: "",
          textColor: "#444444",
          textOpacity: 70,
          icons: [],
          links: [],
          palette: defaultPalette,
          theme: "classic",
          banner: null,
          public: true,
          ghostMode: {
            enabled: false,
            password: "",
            failedAttempts: {},
            lastPasswordUpdate: null,
          },
        },
      });

      // Create username document with lowercase username
      await setDoc(doc(db, "usernames", lowerUsername), {
        userId: userCredential.user.uid,
        createdAt: new Date(),
      });

      // Send verification email
      await sendEmailVerification(userCredential.user, {
        url: 'https://linkist.se/custom-auth-action',
        handleCodeInApp: true
      });

      // Set verification success to show message
      setVerificationSuccess(true);

      // Keep email for showing in the verification message
      setVerificationEmail(email);

      // Set a timeout to sign out the user after 5 seconds
      setTimeout(async () => {
        try {
          await signOut(auth);
          setCurrentUser(null);
          // Keep the verification success message visible
        } catch (error) {
          console.error("Error during delayed sign out:", error);
        }
      }, 5000); // 5 seconds delay

      // Navigate to a verification success page
      navigate("/verification-success");
    } catch (error) {
      console.error("Sign Up Error:", error);
      setError(formatFirebaseError(error));
    } finally {
      setLoading(false);
    }
  };

  /*
   * Google Sign In
   */
  const signInWithGoogle = async () => {
    setLoading(true);
    setError("");
    try {
      console.log("Starting Google sign in");

      // Set auth domain for this session
      if (typeof window !== "undefined" && window.sessionStorage) {
        sessionStorage.setItem(
          "firebase:AuthEventDomain",
          window.location.hostname
        );
      }

      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      const googleProvider = new GoogleAuthProvider();
      googleProvider.addScope("profile");
      googleProvider.addScope("email");
      googleProvider.setCustomParameters({
        prompt: "select_account",
      });

      if (isMobile) {
        console.log("Mobile device detected, using redirect");
        localStorage.setItem("googleSignInPending", "true");
        await signInWithRedirect(auth, googleProvider);
        return;
      } else {
        console.log("Desktop device, using popup");
        const result = await signInWithPopup(auth, googleProvider);

        // Check if user exists in Firestore
        const userDoc = await getDoc(doc(db, "users", result.user.uid));

        if (userDoc.exists()) {
          // User exists, proceed to dashboard
          navigate("/dashboard");
        } else {
          // New user - needs to complete registration
          setError("Please complete your registration with a username");
          await signOut(auth);
        }
      }
    } catch (error) {
      console.error("Google Sign In Error:", error);
      setError(formatFirebaseError(error));
    } finally {
      setLoading(false);
    }
  };

  /*
   * Google Sign Up
   */
  const signUpWithGoogle = async (username) => {
    setLoading(true);
    setError("");
    try {
      // Store lowercase version of username
      const lowerUsername = username.trim().toLowerCase();
      const originalUsername = username.trim(); // Keep original for display
      
      // Validate username
      const usernameValidation = validateUsername(username);
      if (!usernameValidation.valid) {
        setError(usernameValidation.message);
        setLoading(false);
        return;
      }

      // Check username availability using lowercase
      console.log("Checking if username is available:", lowerUsername);
      const usernameDoc = await getDoc(doc(db, "usernames", lowerUsername));
      if (usernameDoc.exists()) {
        setError("Username is already taken");
        setLoading(false);
        return;
      }

      // Set auth domain for this session
      if (typeof window !== "undefined" && window.sessionStorage) {
        sessionStorage.setItem(
          "firebase:AuthEventDomain",
          window.location.hostname
        );
      }

      // Store lowercase username for after redirect
      localStorage.setItem("pendingGoogleSignupUsername", lowerUsername);
      localStorage.setItem("pendingGoogleDisplayUsername", originalUsername);

      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      const googleProvider = new GoogleAuthProvider();
      googleProvider.addScope("profile");
      googleProvider.addScope("email");
      googleProvider.setCustomParameters({
        prompt: "select_account",
      });

      if (isMobile) {
        console.log("Mobile device, using redirect");
        localStorage.setItem("googleSignInPending", "true");
        await signInWithRedirect(auth, googleProvider);
      } else {
        console.log("Desktop device, using popup");
        const result = await signInWithPopup(auth, googleProvider);

        // Create user document with lowercase username
        await setDoc(doc(db, "users", result.user.uid), {
          username: lowerUsername,
          displayUsername: originalUsername, // Store original case for display
          email: result.user.email,
          photoURL: result.user.photoURL || "",
          createdAt: new Date(),
          tierType: "free",
          emailVerified: false,
          profile: {
            displayName: "",
            bio: "",
            textColor: "#444444",
            textOpacity: 70,
            icons: [],
            links: [],
            palette: defaultPalette,
            theme: "classic",
            banner: null,
            public: true,
            ghostMode: {
              enabled: false,
              password: "",
              failedAttempts: {},
              lastPasswordUpdate: null,
            },
          },
        });

        // Create username document with lowercase username
        await setDoc(doc(db, "usernames", lowerUsername), {
          userId: result.user.uid,
          createdAt: new Date(),
        });

        localStorage.removeItem("pendingGoogleSignupUsername");
        localStorage.removeItem("pendingGoogleDisplayUsername");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Google Sign Up Error:", error);
      setError(formatFirebaseError(error));
    } finally {
      setLoading(false);
    }
  };

  /*
   * Password Reset
   */
  const resetPassword = async (email) => {
    setLoading(true);
    setError("");
    try {
      // Use the same ActionCodeSettings as your script
      const actionCodeSettings = {
        url: 'https://linkist.se/custom-auth-action',
        handleCodeInApp: true
      };
  
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      return true;
    } catch (error) {
      console.error("Reset Password Error:", error);
      setError(formatFirebaseError(error));
      return false;
    } finally {
      setLoading(false);
    }
  };

  /*
   * Send Verification Email
   */
  const sendVerificationEmail = async () => {
    setLoading(true);
    setError("");
    setVerificationSuccess(false);

    try {
      // We need to temporarily sign in to send a verification email
      console.log("Sending verification email - temporary auth required");

      // Here we would normally need to sign in again, but for the verification page
      // we'll just show a success message and let the user know they need to sign in

      setVerificationSuccess(true);
      return true;
    } catch (error) {
      console.error("Error sending verification email:", error);
      setError("Failed to send verification email: " + error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  /*
   * Log Out
   */
  const logOut = async () => {
    setError("");
    try {
      console.log("Signing out user");
      await signOut(auth);
      setCurrentUser(null);
      setUserDetails(null);

      // Clear all auth-related localStorage items
      const authItems = [
        "pendingGoogleSignupUsername",
        "pendingGoogleDisplayUsername",
        "googleSignInPending",
        "sessionExpiry",
        "redirectComplete",
      ];

      console.log("Clearing localStorage items:", authItems);
      authItems.forEach((item) => localStorage.removeItem(item));

      navigate("/");
    } catch (error) {
      console.error("Error during sign out:", error);
      setError("Failed to log out");
    }
  };

  // Handle verification prompt actions
  const handleVerificationPrompt = async (confirmed) => {
    if (confirmed) {
      try {
        setLoading(true);
        
        // Actual email sending is handled in the VerificationPrompt component
        // We just need to update the state here
        
        // Show countdown after 8 seconds
        setTimeout(() => {
          setShowVerificationPrompt(false);
          setVerificationPending(false);
        }, 8000);
      } catch (error) {
        console.error("Error in verification prompt:", error);
        setError("An error occurred. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      // User declined, go back to sign in
      setShowVerificationPrompt(false);
    }
  };

  // Create the context value object with all auth methods
  const value = {
    currentUser,
    userDetails,
    loading,
    error,
    verificationPending,
    verificationEmail,
    verificationSuccess,
    showVerificationPrompt,
    setVerificationPending,
    setVerificationEmail,
    setVerificationSuccess,
    setShowVerificationPrompt,
    setError,
    resetState,
    signIn,
    signInWithGoogle,
    signUp,
    signUpWithGoogle,
    resetPassword,
    sendVerificationEmail,
    handleVerificationPrompt,
    logOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected route component
export const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  console.log("ProtectedRoute check:", {
    isLoading: loading,
    hasUser: !!currentUser,
    isVerified: currentUser?.emailVerified,
  });

  // Show loading screen while checking auth status
  if (loading) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  // If no user is logged in, redirect to landing page
  if (!currentUser) {
    console.log("No user, redirecting to landing page");
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check if user is verified or Google user
  const isGoogleUser =
    currentUser.providerData &&
    currentUser.providerData.some((p) => p.providerId === "google.com");

  if (!currentUser.emailVerified && !isGoogleUser) {
    console.log("User not verified, redirecting to verification notice");
    return <Navigate to="/verification-notice" replace />;
  }

  // User is logged in and verified (or Google), allow access
  return children;
};

// Loading screen component
export const LoadingScreen = ({ message = "Loading..." }) => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#000000",
      }}
    >
      <div style={{ textAlign: "center" }}>
      <img 
  src={window.location.origin + "/logowee.png"} 
  alt="Linkist" 
  style={{ 
    width: "75px", 
    height: "auto", 
    marginBottom: "20px" 
  }} 
/>
        <div className="loading-spinner"></div>
        <p style={{ color: "#ffffff", marginTop: "20px" }}>{message}</p>
      </div>
    </div>
  );
};
